import React, { useRef, useEffect } from "react";

/**
 * AIFinanceCanvas — animated hero background.
 * Combines:
 *   - Subtle moving grid (terminal feel)
 *   - Animated multi-line stock chart with glowing edge
 *   - Floating "neural" nodes with pulse + connecting lines (AI net)
 *   - Faint scrolling tickers (numbers)
 */
export default function AIFinanceCanvas() {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let w = 0;
    let h = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    // Neural nodes
    const NODE_COUNT = 26;
    const nodes = Array.from({ length: NODE_COUNT }).map(() => ({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.00015,
      vy: (Math.random() - 0.5) * 0.00015,
      r: 1.2 + Math.random() * 1.6,
      pulse: Math.random() * Math.PI * 2,
    }));

    // Chart series: 3 lines simulating prices
    const series = [
      { color: "rgba(16,185,129,0.85)", base: 0.55, amp: 0.10, freq: 0.0019, phase: 0, speed: 0.00045, glow: "rgba(16,185,129,0.55)" },
      { color: "rgba(96,165,250,0.85)", base: 0.62, amp: 0.07, freq: 0.0026, phase: 1.2, speed: 0.00035, glow: "rgba(96,165,250,0.5)" },
      { color: "rgba(248,250,252,0.55)", base: 0.48, amp: 0.05, freq: 0.0034, phase: 2.4, speed: 0.00055, glow: "rgba(255,255,255,0.4)" },
    ];

    // Floating numeric ticker tokens
    const tickerTokens = [
      "▲ 0.42%",
      "▼ 0.18%",
      "AAPL 226.71",
      "BTC 64,210",
      "NIFTY 24,612",
      "SENSEX 81,420",
      "EUR/USD 1.082",
      "ROU 1,284,560",
      "ESOP σ 38.4",
      "IFRS 16",
      "Ind AS 116",
      "DLOM 14.2%",
      "WACC 11.7%",
      "EBITDA ▲",
    ];
    const tickers = Array.from({ length: 14 }).map((_, i) => ({
      text: tickerTokens[i % tickerTokens.length],
      x: Math.random(),
      y: Math.random(),
      speed: 0.00005 + Math.random() * 0.00012,
      opacity: 0.18 + Math.random() * 0.22,
    }));

    let t0 = performance.now();

    const draw = (now) => {
      const t = now - t0;
      ctx.clearRect(0, 0, w, h);

      // Background gradient
      const bg = ctx.createLinearGradient(0, 0, 0, h);
      bg.addColorStop(0, "#0b1220");
      bg.addColorStop(0.55, "#0f1a2c");
      bg.addColorStop(1, "#070b14");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // Soft radial highlight
      const rg = ctx.createRadialGradient(w * 0.5, h * 0.55, 0, w * 0.5, h * 0.55, Math.max(w, h) * 0.6);
      rg.addColorStop(0, "rgba(56,189,248,0.10)");
      rg.addColorStop(0.4, "rgba(56,189,248,0.04)");
      rg.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = rg;
      ctx.fillRect(0, 0, w, h);

      // Moving grid
      const gridSize = 56;
      const offset = (t * 0.012) % gridSize;
      ctx.strokeStyle = "rgba(148,163,184,0.07)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = -gridSize + offset; x < w + gridSize; x += gridSize) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
      }
      for (let y = -gridSize + offset; y < h + gridSize; y += gridSize) {
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
      }
      ctx.stroke();

      // Tickers (faint numerical noise)
      ctx.font = "11px 'JetBrains Mono', monospace";
      tickers.forEach((tk) => {
        tk.y += tk.speed;
        if (tk.y > 1.05) {
          tk.y = -0.05;
          tk.x = Math.random();
          tk.text = tickerTokens[Math.floor(Math.random() * tickerTokens.length)];
        }
        ctx.fillStyle = `rgba(148,197,255,${tk.opacity})`;
        ctx.fillText(tk.text, tk.x * w, tk.y * h);
      });

      // Stock chart series
      series.forEach((s) => {
        ctx.beginPath();
        const steps = 80;
        const phase = s.phase + t * s.speed;
        for (let i = 0; i <= steps; i++) {
          const px = (i / steps) * w;
          const noise =
            Math.sin(i * s.freq * 80 + phase) * 0.5 +
            Math.sin(i * s.freq * 220 + phase * 1.3) * 0.25 +
            Math.sin(i * s.freq * 540 + phase * 2.1) * 0.12;
          const py = (s.base + s.amp * noise) * h;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        // Glow
        ctx.shadowColor = s.glow;
        ctx.shadowBlur = 18;
        ctx.strokeStyle = s.color;
        ctx.lineWidth = 1.4;
        ctx.stroke();
        ctx.shadowBlur = 0;
      });

      // Highlight last point of the first (emerald) series
      const sMain = series[0];
      const phaseMain = sMain.phase + t * sMain.speed;
      const noiseEnd =
        Math.sin(80 * sMain.freq * 80 + phaseMain) * 0.5 +
        Math.sin(80 * sMain.freq * 220 + phaseMain * 1.3) * 0.25 +
        Math.sin(80 * sMain.freq * 540 + phaseMain * 2.1) * 0.12;
      const lastY = (sMain.base + sMain.amp * noiseEnd) * h;
      ctx.beginPath();
      ctx.fillStyle = "rgba(16,185,129,1)";
      ctx.shadowColor = "rgba(16,185,129,0.9)";
      ctx.shadowBlur = 20;
      ctx.arc(w - 6, lastY, 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;

      // Update + draw nodes (neural net)
      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > 1) n.vx *= -1;
        if (n.y < 0 || n.y > 1) n.vy *= -1;
        n.pulse += 0.02;
      });

      // Connections
      const maxDist = 0.18;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < maxDist) {
            const o = (1 - d / maxDist) * 0.45;
            ctx.strokeStyle = `rgba(125,211,252,${o.toFixed(3)})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(a.x * w, a.y * h);
            ctx.lineTo(b.x * w, b.y * h);
            ctx.stroke();
          }
        }
      }

      // Nodes themselves
      nodes.forEach((n) => {
        const pulse = (Math.sin(n.pulse) + 1) * 0.5;
        ctx.beginPath();
        ctx.fillStyle = `rgba(186,230,253,${0.55 + pulse * 0.4})`;
        ctx.shadowColor = "rgba(56,189,248,0.7)";
        ctx.shadowBlur = 8 + pulse * 6;
        ctx.arc(n.x * w, n.y * h, n.r + pulse * 0.8, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      // Vignette
      const vg = ctx.createRadialGradient(
        w * 0.5,
        h * 0.5,
        Math.min(w, h) * 0.35,
        w * 0.5,
        h * 0.5,
        Math.max(w, h) * 0.85,
      );
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(0,0,0,0.55)");
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, w, h);

      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 h-full w-full block"
      data-testid="ai-finance-canvas"
      aria-hidden="true"
    />
  );
}
